import { ProgramNames } from '../../../models/programs/Programs'
import { PROVIDER_TYPES } from '../../../models/providers/Providers'
import { PROGRAM_TAXONOMY, ProgramCustomerPropertyId } from '../../common/constants/customerProgram'
import { TREATMENT_OPTIONS } from '../../common/constants/treatmentOptions'
import { LTSessionTypes } from '../../sessions/constants'
import { ConsentText, ProgramDisplayLabels } from '../messages'
import { ProgramConfig } from '../types'

export const COACHING_PROGRAM = {
  programName: ProgramNames.Coaching,
  blendedCareProgramId: '7ec82ca3-843f-4996-bdf9-49d8c7a35ed5',
  customerPropertyId: ProgramCustomerPropertyId.stressManagement,
  selfPayEligible: true,
  isCoachingProgram: true,

  displayLabels: {
    default: ProgramDisplayLabels.coaching,
    sessionSummary: 'coaching',
  },

  associatedProviderTypes: [PROVIDER_TYPES.COACH.lyraType],
  associatedProviderModalities: ['stressManagement'],

  canRescheduleAppointment: true,

  sessionTypeConfig: {
    sessionType: LTSessionTypes.SMP_SESSION,
    internationalSessionType: LTSessionTypes.INTL_SMP_SESSION,
    rateStrategy: 'lookupSessionRate',
  },

  directBookingParams: {
    clientele: PROGRAM_TAXONOMY.clientele.individual,
    treatment: PROGRAM_TAXONOMY.treatment.coaching,
    partner: PROGRAM_TAXONOMY.partner.blendedCare,
    offering: PROGRAM_TAXONOMY.offering.default,
  },

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.COACHING],
    programTaxonomyTreatmentOptions: [
      {
        clientele: PROGRAM_TAXONOMY.clientele.individual,
        treatment: PROGRAM_TAXONOMY.treatment.coaching,
        partner: PROGRAM_TAXONOMY.partner.blendedCare,
        offering: PROGRAM_TAXONOMY.offering.default,
      },
      {
        clientele: PROGRAM_TAXONOMY.clientele.individual,
        treatment: PROGRAM_TAXONOMY.treatment.coaching,
        partner: PROGRAM_TAXONOMY.partner.blendedCare,
        offering: PROGRAM_TAXONOMY.offering.neurodiversitySkills,
      },
    ],
    isFullTreatmentOption: true,
  },

  consents: [
    {
      consentText: ConsentText.coachingConsent,
      consentUrl: 'https://www.lyrahealth.com/bcc-agreement',
      showConsentIf: (options) => options?.isInternational === false,
    },
    {
      consentText: ConsentText.coachingConsent,
      consentUrl: 'https://www.lyrahealth.com/bcc-agreement-2',
      showConsentIf: (options) => options?.isInternational === true,
    },
  ],

  noteContentMetadataName: {
    INITIAL_NOTE: 'bccInitialNote',
    SESSION_NOTE: 'bccSessionNote',
    GENERAL_NOTE: 'baseGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
    OLD_INITIAL_NOTE: 'coachingInitialNote',
    OLD_SESSION_NOTE: 'coachingSessionNote',
    OLD_GENERAL_NOTE: 'coachingGeneralNote',
    OLD_REFERRAL_NOTE: 'coachingReferralNote',
  },
} satisfies ProgramConfig

export const COACHING_FOR_PARENTS_PROGRAM = {
  programName: ProgramNames.CoachingForParents,
  blendedCareProgramId: '5ee0996e-c7cd-46d1-b009-462f6379cbd5',
  customerPropertyId: ProgramCustomerPropertyId.coachingForParents,
  isCoachingProgram: true,

  displayLabels: {
    default: ProgramDisplayLabels.coachingForParents,
    careType: ProgramDisplayLabels.coaching,
    short: 'CFP',
    sessionSummary: 'coaching',
  },

  canRescheduleAppointment: true,
  selfPayEligible: true,

  sessionTypeConfig: {
    sessionType: LTSessionTypes.COACHING_FOR_PARENTS_SESSION,
    rateStrategy: 'lookupSessionRate',
  },

  directBookingParams: {
    clientele: PROGRAM_TAXONOMY.clientele.individual,
    treatment: PROGRAM_TAXONOMY.treatment.coaching,
    partner: PROGRAM_TAXONOMY.partner.blendedCare,
    offering: PROGRAM_TAXONOMY.offering.parenting,
  },

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.COACHING_FOR_PARENTS],
    programTaxonomyTreatmentOptions: [
      {
        clientele: PROGRAM_TAXONOMY.clientele.individual,
        treatment: PROGRAM_TAXONOMY.treatment.coaching,
        partner: PROGRAM_TAXONOMY.partner.blendedCare,
        offering: PROGRAM_TAXONOMY.offering.parenting,
      },
    ],
    isFullTreatmentOption: true,
  },

  associatedProviderTypes: [PROVIDER_TYPES.COACH.lyraType],
  associatedProviderModalities: ['coachingForParents'],

  consents: [
    {
      consentText: ConsentText.coachingConsent,
      consentUrl: 'https://www.lyrahealth.com/bcc-agreement',
      showConsentIf: (options) => options?.isInternational === false,
    },
    {
      consentText: ConsentText.coachingConsent,
      consentUrl: 'https://www.lyrahealth.com/bcc-agreement-2',
      showConsentIf: (options) => options?.isInternational === true,
    },
  ],

  noteContentMetadataName: {
    INITIAL_NOTE: 'bccInitialNote',
    SESSION_NOTE: 'bccSessionNote',
    GENERAL_NOTE: 'baseGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
  },
} satisfies ProgramConfig

// CPR-2416 TODO VERIFY: This config is copied from COACHING_PROGRAM with Performance Coaching CTPO.
// Confirm blendedCareProgramId, sessionTypeConfig, and consents with the backend team.
export const PERFORMANCE_COACHING_PROGRAM = {
  programName: ProgramNames.PerformanceCoaching,
  blendedCareProgramId: null,
  customerPropertyId: ProgramCustomerPropertyId.stressManagement,
  selfPayEligible: true,
  isCoachingProgram: true,

  displayLabels: {
    default: ProgramDisplayLabels.performanceCoaching,
    sessionSummary: 'coaching',
  },

  associatedProviderTypes: [PROVIDER_TYPES.COACH.lyraType],
  associatedProviderModalities: ['stressManagement'],

  canRescheduleAppointment: true,

  directBookingParams: {
    clientele: PROGRAM_TAXONOMY.clientele.individual,
    treatment: PROGRAM_TAXONOMY.treatment.coaching,
    partner: PROGRAM_TAXONOMY.partner.blendedCare,
    offering: PROGRAM_TAXONOMY.offering.performance,
  },

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.COACHING],
    programTaxonomyTreatmentOptions: [
      {
        clientele: PROGRAM_TAXONOMY.clientele.individual,
        treatment: PROGRAM_TAXONOMY.treatment.coaching,
        partner: PROGRAM_TAXONOMY.partner.blendedCare,
        offering: PROGRAM_TAXONOMY.offering.performance,
      },
    ],
    isFullTreatmentOption: false,
  },

  consents: [
    {
      consentText: ConsentText.coachingConsent,
      consentUrl: 'https://www.lyrahealth.com/bcc-agreement',
      showConsentIf: (options) => options?.isInternational === false,
    },
    {
      consentText: ConsentText.coachingConsent,
      consentUrl: 'https://www.lyrahealth.com/bcc-agreement-2',
      showConsentIf: (options) => options?.isInternational === true,
    },
  ],
} satisfies ProgramConfig

export const SINGLE_SESSION_COACHING_PROGRAM = {
  programName: ProgramNames.SingleSessionCoaching,
  blendedCareProgramId: 'deb574e4-6aa3-4b47-bd21-d8be3ba5d42b',
  customerPropertyId: ProgramCustomerPropertyId.guidedSelfCareEnabled,
  isCoachingProgram: true,

  displayLabels: {
    default: ProgramDisplayLabels.guidedSelfCare,
    clientProfile: 'Guided self-care coaching',
    short: 'GSC',
    sessionSummary: 'coaching',
  },

  associatedProviderTypes: [PROVIDER_TYPES.COACH.lyraType],
  associatedProviderModalities: ['singleSessionCoaching'],

  canRescheduleAppointment: true,
  selfPayEligible: false,

  sessionTypeConfig: {
    sessionType: LTSessionTypes.SSC_SESSION,
    rateStrategy: 'lookupSessionRate',
  },

  directBookingParams: {
    clientele: PROGRAM_TAXONOMY.clientele.individual,
    treatment: PROGRAM_TAXONOMY.treatment.coaching,
    partner: PROGRAM_TAXONOMY.partner.blendedCare,
    offering: PROGRAM_TAXONOMY.offering.singleSession,
  },

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.GUIDED_SELF_CARE],
    programTaxonomyTreatmentOptions: [
      {
        clientele: PROGRAM_TAXONOMY.clientele.individual,
        treatment: PROGRAM_TAXONOMY.treatment.coaching,
        partner: PROGRAM_TAXONOMY.partner.blendedCare,
        offering: PROGRAM_TAXONOMY.offering.singleSession,
      },
    ],
    isFullTreatmentOption: true,
  },

  consents: [
    {
      consentText: ConsentText.guidedSelfCareConsent,
      consentUrl: 'https://www.lyrahealth.com/gsc-agreement',
      showConsentIf: () => true,
    },
  ],

  noteContentMetadataName: {
    INITIAL_NOTE: 'sscInitialNote',
    SESSION_NOTE: 'sscSessionNote',
    GENERAL_NOTE: 'baseGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
    OLD_INITIAL_NOTE: 'singleSessionCoachingInitialNote',
    OLD_SESSION_NOTE: 'singleSessionCoachingSessionNote',
    OLD_GENERAL_NOTE: 'singleSessionCoachingGeneralNote',
    OLD_REFERRAL_NOTE: 'singleSessionCoachingReferralNote',
  },
} satisfies ProgramConfig
